// src/pages/TermsOfService.js
import React from 'react';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import '../styles.css';

export default function TermsOfService() {
  return (
    <>
      <PublicHeader />
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        <h1>Terms of Service</h1>
        <p><em>Last updated: February 6, 2026</em></p>

        <h2>1. Acceptance of Terms</h2>
        <p>By accessing and using Hot Setup ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>

        <h2>2. Description of Service</h2>
        <p>Hot Setup is a web-based application designed to help motorsports participants track vehicle setup changes, manage session data, and optimize performance. The service is provided on a subscription basis with both free and premium tiers.</p>

        <h2>3. Subscription Terms</h2>
        <h3>3.1 Subscription Plans</h3>
        <p>We offer various subscription plans as detailed on our pricing page. Subscription fees are billed in advance on a recurring basis (monthly or annually).</p>

        <h3>3.2 Cancellation</h3>
        <p>You may cancel your subscription at any time through your account settings or by contacting our support team. Upon cancellation, you will continue to have access to the service until the end of your current billing period. After this period, your access will be terminated, and you will no longer be able to use premium features.</p>

        <h3>3.3 Refunds</h3>
        <p>All subscription fees are non-refundable. We do not provide refunds for any unused portion of a subscription period.</p>

        <h3>3.4 Lifetime Access</h3>
        <p>Lifetime purchases provide access to the service for as long as Hot Setup continues to operate. If the service ceases to exist or is discontinued, your access will terminate. However, you will retain the ability to download and export your data prior to service termination.</p>

        <h3>3.4 Price Changes</h3>
        <p>We reserve the right to change subscription prices at any time. Price changes will be communicated in advance and will not affect current subscription periods.</p>

        <h2>4. Payment Terms</h2>
        <p>Payment is processed securely through our payment processor. You agree to provide accurate and complete billing information. Failure to pay subscription fees may result in suspension or termination of your account.</p>

        <h2>5. User Obligations</h2>
        <h3>5.1 Account Responsibility</h3>
        <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>

        <h3>5.2 Acceptable Use</h3>
        <p>You agree to use the service only for lawful purposes and in accordance with these terms. You may not use the service to violate any laws or infringe on the rights of others.</p>

        <h3>5.3 Data Accuracy</h3>
        <p>You are responsible for the accuracy of data entered into the service. We are not liable for any losses resulting from inaccurate data.</p>

        <h3>5.4 Data Backup and Loss</h3>
        <p>You are solely responsible for backing up your data. While we provide data export capabilities, we strongly recommend that you regularly download and backup your setup data to prevent any potential loss. We are not liable for any data loss, corruption, or inability to recover data.</p>

        <h3>5.5 Prohibition of System Abuse</h3>
        <p><strong>We prohibit attempts to circumvent subscription or trial limitations by exporting and subsequently importing backup data to indefinitely extend access periods. We reserve the right to delete all user data and permanently ban such users from the application.</strong></p>

        <h2>6. Intellectual Property</h2>
        <p>The service and its original content, features, and functionality are and will remain the exclusive property of Hot Setup and its licensors. The service is protected by copyright, trademark, and other laws.</p>

        <h2>7. Privacy Policy</h2>
        <p>Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the service, to understand our practices.</p>

        <h2>8. Disclaimers</h2>
        <p>The service is provided on an "as is" and "as available" basis. We make no warranties, expressed or implied, and hereby disclaim and negate all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>
        <p>We do not guarantee that the service will be available at all times or be uninterrupted. We are not responsible for any losses, damages, or liability that may result from service outages, downtime, or unavailability.</p>

        <h2>9. Limitation of Liability</h2>
        <p>In no event shall Hot Setup or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the service, even if Hot Setup or our authorized representative has been notified orally or in writing of the possibility of such damage.</p>

        <h2>10. Termination</h2>
        <p>We may terminate or suspend your account and bar access to the service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.</p>

        <h2>11. Governing Law</h2>
        <p>These terms shall be interpreted and governed by the laws of the jurisdiction in which Hot Setup operates, without regard to conflict of law provisions.</p>

        <h2>12. Changes to Terms</h2>
        <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.</p>

        <h2>13. Contact Information</h2>
        <p>If you have any questions about these Terms of Service, please contact us at hello@hotsetup.app.</p>

        <h1>Privacy Policy</h1>
        <p><em>Last updated: February 6, 2026</em></p>

        <h2>1. Information We Collect</h2>
        <h3>1.1 Personal Information</h3>
        <p>We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This may include:</p>
        <ul>
          <li>Name and email address</li>
          <li>Payment information (processed securely by third-party providers)</li>
          <li>Account credentials</li>
        </ul>

        <h3>1.2 Usage Data</h3>
        <p>We automatically collect certain information when you use our service:</p>
        <ul>
          <li>Vehicle setup data and configurations</li>
          <li>Session logs and performance data</li>
          <li>Device information and usage patterns</li>
          <li>IP addresses and location data</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our services</li>
          <li>Process transactions and manage subscriptions</li>
          <li>Send you technical notices and support communications</li>
          <li>Respond to your comments and questions</li>
          <li>Analyze usage patterns to improve our service</li>
        </ul>

        <h2>3. Information Sharing</h2>
        <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy:</p>
        <ul>
          <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our service (payment processors, cloud hosting, analytics)</li>
          <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and safety</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
        </ul>

        <h2>4. Data Security</h2>
        <p>We take reasonable measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure. We make every effort to keep your data private and secure, but cannot guarantee absolute security.</p>

        <h2>5. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.</p>

        <h2>6. Your Rights</h2>
        <h3>6.1 Access and Control</h3>
        <p>You have the right to:</p>
        <ul>
          <li>Access the personal information we hold about you</li>
          <li>Correct inaccurate or incomplete information</li>
          <li>Request deletion of your personal information</li>
          <li>Object to or restrict certain processing of your information</li>
        </ul>

        <h3>6.2 Account Deletion</h3>
        <p>You can delete all of your data at any time by closing your account. When you request account deletion, we will permanently remove all your personal information, setup data, and account details from our systems. This action cannot be undone.</p>

        <h2>7. Cookies and Tracking</h2>
        <p>We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookie settings through your browser preferences.</p>

        <h2>8. Third-Party Services</h2>
        <p>Our service may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these external services. We encourage you to review their privacy policies.</p>

        <h2>9. Children's Privacy</h2>
        <p>Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it.</p>

        <h2>10. International Data Transfers</h2>
        <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.</p>

        <h2>11. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.</p>

        <h2>12. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy or our data practices, please contact us at hello@hotsetup.app.</p>
      </div>
      <Footer />
    </>
  );
}